"use client";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { $getNodeByKey, type NodeKey } from "lexical";
import type * as React from "react";
import { Suspense, useCallback, useRef, useState } from "react";
import { cn } from "~/lib/utils";
import ImageResizer from "../ImageResizer";
import { $isImageNode } from "./ImageNode";

export default function ImageComponent({
  src,
  altText,
  width,
  height,
  maxWidth,
  nodeKey,
  showCaption,
  caption,
}: {
  altText: string;
  caption?: string;
  height?: number;
  maxWidth?: number;
  nodeKey: NodeKey;
  showCaption?: boolean;
  src: string;
  width?: number;
}): React.JSX.Element {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [editor] = useLexicalComposerContext();

  const handleOnClick = useCallback(
    (event: React.MouseEvent) => {
      if (!editor.isEditable()) {
        return;
      }
      if (isResizing) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.shiftKey) {
        setSelected(!isSelected);
      } else {
        if (!isSelected) {
          clearSelection();
          setSelected(true);
        }
      }
    },
    [isResizing, isSelected, setSelected, clearSelection, editor],
  );

  const onResizeEnd = (
    nextWidth: "inherit" | number,
    nextHeight: "inherit" | number,
  ) => {
    // Delay hiding resizer handles for one better rendering cycle
    setTimeout(() => {
      setIsResizing(false);
    }, 200);

    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) {
        node.setWidthAndHeight(nextWidth, nextHeight);
      }
    });
  };

  const onResizeStart = () => {
    setIsResizing(true);
  };

  const isFocused = isSelected || isResizing;

  return (
    <Suspense fallback={null}>
      {/** biome-ignore lint/a11y/useSemanticElements: interactive enough */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleOnClick(e as unknown as React.MouseEvent);
          }
        }}
        onClick={handleOnClick}
        className={cn(
          "relative inline-block rounded-lg group cursor-default border-none p-0 bg-transparent m-0 outline-none",
          {
            "ring-2 ring-primary ring-offset-2": isFocused,
          },
        )}
      >
        {/* biome-ignore lint/performance/noImgElement: Manual resizing implementation requires direct img element access */}
        {/** biome-ignore lint/a11y/useKeyWithClickEvents: accessible enough */}
        <img
          ref={imageRef}
          src={src}
          alt={altText}
          onClick={handleOnClick}
          style={{
            height: height || "auto",
            maxWidth: maxWidth || "100%",
            width: width || "auto",
          }}
          draggable="false"
          className={cn("block rounded-lg cursor-pointer", {
            "h-auto": !height,
            "max-w-full": !width,
          })}
        />
        {showCaption && caption && (
          <div className="mt-2 text-sm text-center text-muted-foreground italic">
            {caption}
          </div>
        )}
        {isFocused && editor.isEditable() && (
          <ImageResizer
            showCaption={!!showCaption}
            setShowCaption={() => {
              editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if ($isImageNode(node)) {
                  node.setShowCaption(!node.getShowCaption());
                }
              });
            }}
            captionsEnabled={true}
            editor={editor}
            buttonRef={buttonRef}
            imageRef={imageRef}
            onResizeStart={onResizeStart}
            onResizeEnd={onResizeEnd}
          />
        )}
      </div>
    </Suspense>
  );
}
